/*
interface FirstSpiritPreviewButtonScope {
  HTMLElement $node,
  HTMLElement $button,
  String previewId,
  Status<any> status,
  String language,
}
*/
export default function ({ preview, actions }) {

  // Edit Button
  preview.registerButton({
    _name: 'edit',
    css: 'tpp-icon-edit',
    getLabel: ({ language }) => language.toLowerCase() === 'de' ? 'Bearbeiten' : 'Edit',
    isVisible: ({ status }) => status.custom === null && (['PageRef', 'Page', 'Section', 'GCAPage', 'GCASection'].includes(status.elementType) || status.elementType.startsWith('Dataset')),
    isEnabled: ({ status }) => status.permissions.change,
    execute: async ({ previewId }) => actions.showEditDialog(previewId),
  });

  // Translation dialogue
  let isMultiLanguage = new Promise(resolve => actions.languages().then(languages => resolve(languages.length > 1)));
  preview.addButton({
    _name: 'translate',
    css: 'tpp-icon-translate',
    isVisible: async({ status }) => ['Page', 'Section', 'Dataset', 'GCAPage'].includes(status.elementType) && await isMultiLanguage,
    isEnabled: ({ status }) => status.permissions.change,
    getItems: async() => {
      const languages = await actions.languages();
      const currentLanguage = await actions.getPreviewLanguage() || languages[0];
      let items = [];
      for (let targetLanguage of languages) {
        if (targetLanguage !== currentLanguage) {
          items.push({ source: currentLanguage, target: targetLanguage });
          }
      }
      items.push(...items.map(({ source: target, target: source }) => ({ source, target })));
      return items.map(({ source, target }) => ({ label: `${source} → ${target}`, source, target }));
    },
    execute: async ({ previewId }, { source, target }) => actions.showTranslationDialog(previewId, source, target)
  });

  // Edit MetaData Button
  preview.addButton({
    _name: 'metadata',
    css: 'tpp-icon-meta-data',
    getLabel: ({ language }) => language.toLowerCase() === 'de' ? 'Meta-Daten' : 'Meta Data',
    isVisible: () => false,
    isEnabled: ({ status }) => status.permissions.changeMeta,
    execute: async ({ previewId }) => actions.showMetaDataDialog(previewId),
  });

  // Add Sibling Section Button
  preview.registerButton({
    _name: 'add-sibling-section',
    css: 'tpp-icon-add-section',
    getLabel: ({ language }) => language.toLowerCase() === 'de' ? 'Absatz hinzufügen' : 'Add Section',
    isVisible: ({ status }) => status.custom === null && status.elementType === 'Section',
    isEnabled: ({ status }) => status.permissions.change,
    execute: async ({ previewId }) => actions.createSection(previewId),
  });

  // Add Section Button
  preview.registerButton({
    _name: 'add-child-section',
    css: 'tpp-icon-add-section',
    getLabel: ({ language }) => language.toLowerCase() === 'de' ? 'Absatz hinzufügen' : 'Add Section',
    isVisible: ({ status }) => status.custom === null && status.elementType === 'Page' && status.children.length !== 0,
    isEnabled: ({ status }) => status.permissions.appendLeaf,
    getItems: ({ status }) => status.children.length === 1 ? [] : status.children.map(body => body.name),
    execute: async ({ previewId, status }, body = status.children[0].name) => actions.createSection(previewId, { body }),
  });
  preview.addButton({
    _name: 'add-child-section-body',
    css: 'tpp-icon-add-section',
    getLabel: ({ language }) => language.toLowerCase() === 'de' ? 'Absatz hinzufügen' : 'Add Section',
    isVisible: ({ status }) => status.custom === null && status.elementType === 'Body',
    isEnabled: ({ status }) => status.permissions.appendLeaf,
    execute: async ({ previewId }) => actions.createSection(previewId),
  });

  // Add Workflow Button
  preview.registerButton({
    _name: 'workflows',
    getIcon: ({ $button, status }) => {
      if (status.workflows.current !== null) {
        $button.classList.add('tpp-icon-workflow-in-progress');
      } else if (status.released) {
        $button.classList.add('tpp-icon-workflow-released');
      } else {
        $button.classList.add('tpp-icon-workflow-changed');
      }
    },
    getLabel: ({ language, status }) => {
      if (status.workflows.current !== null) {
        return status.workflows.current.displayName;
      } else {
        return language.toLowerCase() === 'de' ? 'Starte Arbeitsablauf' : 'Start Workflow';
      }
    },
    isVisible: async ({ status, previewId }) => {
      if (status.custom === null && status.releaseSupported) {
        if (previewId !== await actions.getPreviewElement()) {
          if (status.workflows.allowed.length !== 0 || status.workflows.current !== null) {
            return true;
          }
        }
      }
      return false;
    },
    isEnabled: ({ status }) => true,
    getItems: ({ status }) => {
      if (status.workflows.current !== null) {
        return status.workflows.transitions.map(transition => {
          const { displayName, id } = transition;
          return { label: displayName, transitionId: id };
        });

      } else {
        return status.workflows.allowed
          .filter(({ uid }) => status.workflows.deleteWorkflow === null || uid !== status.workflows.deleteWorkflow.uid)
          .map(workflow => ({ label: workflow.displayName, workflowId: workflow.id }));
      }
    },
    execute: async ({ previewId }, { workflowId = null, transitionId = null } = {}) => {
      if (workflowId !== null) {
        return actions.startWorkflow(previewId, workflowId);
      } else if (transitionId !== null) {
        return actions.processWorkflow(previewId, transitionId);
      }
    },
  });

  // Add Delete Button
  preview.registerButton({
    _name: 'delete',
    css: 'tpp-icon-delete',
    getLabel: ({ language }) => language.toLowerCase() === 'de' ? 'Löschen' : 'Remove',
    isVisible: ({ status }) => status.custom === null && !['Body', 'Media'].includes(status.elementType),
    isEnabled: ({ status }) => {
      if (!['Section'].includes(status.elementType) && status.workflows.deleteWorkflow !== null) {
        return status.workflows.current === null;
      } else {
        return status.permissions.delete;
      }
    },
    execute: async ({ previewId, status }) => {
      if (!['Section'].includes(status.elementType) && status.workflows.deleteWorkflow !== null) {
      	return actions.startWorkflow(previewId, status.workflows.deleteWorkflow.uid);
      } else {
      	return actions.deleteElement(previewId, true);
      }
    }
  });

  // Crop Dialog
  preview.registerButton({
    _name: 'crop',
    css: 'tpp-icon-crop-image',
    isVisible: ({ status }) => status.custom === null && status.elementType === 'Media',
    isEnabled: ({ status }) => status.permissions.change,
    execute: async ({ previewId }) => actions.cropImage(previewId),
  });

  // Toggle Bookmark
  preview.registerButton({
    _name: 'bookmark',
    isVisible: ({ status }) => status.custom === null && ['Page', 'PageRef', 'Section', 'Dataset'].includes(status.elementType),
    isEnabled: () => true,
    getIcon: ({ $button, status }) => $button.classList.add(status.bookmark ? 'tpp-icon-bookmark-delete' : 'tpp-icon-bookmark-create'),
    getLabel: ({ language }) => language.toLowerCase() === 'de' ? 'Kopiervorlage' : 'Bookmark',
    execute: async ({ previewId }) => actions.toggleBookmark(previewId),
  });
};
